<script>
  import { locale, t, publicPath } from '../i18n';
  import { getFaq } from '../content/publicSite.js';
  import LanguageSelector from '../components/LanguageSelector.svelte';
  import LoginPanel from '../components/LoginPanel.svelte';
  import AccountBenefits from '../components/AccountBenefits.svelte';
  import AccountActivity from '../components/AccountActivity.svelte';
  import { signOutSession } from '../utils/authSession';
  import { onMount } from 'svelte';
  import { sessionStore, logout, updateRionBalance } from '../stores';
  import { getRionBalance, sendContactEmail } from '../utils/apiClient';
  import { get } from 'svelte/store';
  import { fly, fade } from 'svelte/transition';

  export let navigateTo;
  export let toggleTheme = () => {};
  export let theme = 'light';
  export let activeTab = 'profile'; // profile, faq, contact

  let rionBalance = $sessionStore.rionBalance || 0;
  let isSendingContact = false;

  // 문의하기 폼
  let contactSubject = '';
  let contactMessage = '';
  let contactFile = null;
  let contactFilePreview = null;

  $: faqs = getFaq($locale);

  async function updateBalance() {
    if (!$sessionStore.loggedIn || !$sessionStore.userId) return;
    try {
      const data = await getRionBalance($sessionStore.userId);
      if (data) {
        updateRionBalance(data.total_rion || data.balance || 0);
        rionBalance = $sessionStore.rionBalance;
      }
    } catch (e) {
      console.error(e);
    }
  }

  onMount(() => {
    updateBalance();
    const timer = setInterval(updateBalance, 30000);
    return () => clearInterval(timer);
  });

  function handleLogout() {
    if (confirm('로그아웃 하시겠습니까?')) {
      signOutSession();
      navigateTo('home');
    }
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (file) {
      contactFile = file;
      const reader = new FileReader();
      reader.onload = (ev) => {
        contactFilePreview = ev.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  async function submitContact() {
    if (!contactMessage.trim()) {
      alert('문의 내용을 입력해주세요.');
      return;
    }
    isSendingContact = true;
    try {
      const res = await sendContactEmail(contactSubject, contactMessage, contactFile);
      if (res.success) {
        alert('문의가 성공적으로 전송되었습니다.');
        contactSubject = '';
        contactMessage = '';
        contactFile = null;
        contactFilePreview = null;
        activeTab = 'profile';
      } else {
        alert(res.error || '전송 실패');
      }
    } catch (e) {
      alert('오류가 발생했습니다.');
    } finally {
      isSendingContact = false;
    }
  }

  const termsUrl = "https://pond-echidna-ecb.notion.site/E-RION-2bf82c606e4180928714fac2b9efbded";
  const privacyUrl = "https://pond-echidna-ecb.notion.site/E-RION-2bf82c606e4180f7b988cfced4b47013";
</script>

<div class="settings-wrapper">
  {#if $sessionStore.loggedIn}
  <div class="settings-sidebar">
    <div class="sidebar-user">
      <div class="avatar">
        {#if $sessionStore.photoURL}
          <img src={$sessionStore.photoURL} alt="avatar" />
        {:else}
          <div class="avatar-ph">{$sessionStore.name?.[0] || 'U'}</div>
        {/if}
      </div>
      <div class="user-meta">
        <div class="user-name">{$sessionStore.name || '방문자'}</div>
        <div class="user-email">{$sessionStore.email || ''}</div>
      </div>
    </div>
    
    <nav class="sidebar-menu">
      <button class:active={activeTab === 'profile'} on:click={() => activeTab = 'profile'}>
        <i class="icon">👤</i> 내 정보
      </button>
      <button class:active={activeTab === 'activity'} on:click={() => activeTab = 'activity'}>이용 내역</button>
      <button class:active={activeTab === 'faq'} on:click={() => activeTab = 'faq'}>
        <i class="icon">❓</i> {$t('faq')}
      </button>
      <button class:active={activeTab === 'contact'} on:click={() => activeTab = 'contact'}>
        <i class="icon">📧</i> {$t('contact')}
      </button>
      <div class="nav-divider"></div>
      <button on:click={() => window.open(termsUrl, '_blank')}>
        <i class="icon">📄</i> {$t('terms')}
      </button>
      <button on:click={() => window.open(privacyUrl, '_blank')}>
        <i class="icon">🔒</i> {$t('privacy')}
      </button>
      <div class="nav-divider"></div>
      <button class="theme-menu-btn" on:click={toggleTheme}>
        <i class="icon">{theme === 'dark' ? '☀️' : '🌙'}</i>
        {theme === 'dark' ? '라이트 모드' : '다크 모드'}
      </button>
    </nav>

    <div class="sidebar-footer">
      <button class="logout-btn" on:click={handleLogout}>{$t('logout')}</button>
    </div>
  </div>
  {/if}

  <div class="settings-main">
    <section class="settings-language" aria-label={$t('languageSettings')}>
      <h2>{$t('languageSettings')}</h2>
      <LanguageSelector />
    </section>
    {#if !$sessionStore.loggedIn}
      <div class="content-card"><LoginPanel /></div>
    {:else if activeTab === 'profile'}
      <div class="content-card" in:fly={{ y: 20 }}>
        <h2>내 계정 정보</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">이름</span>
            <span class="value">{$sessionStore.name || '-'}</span>
          </div>
          <div class="info-item">
            <span class="label">이메일</span>
            <span class="value">{$sessionStore.email || '-'}</span>
          </div>
        </div>

        <div class="rion-box">
          <div class="rion-header">
            <img src="/images/rion_icon.png" alt="rion" />
            <span>보유 RION</span>
          </div>
          <div class="rion-balance">
            <span class="num">{$sessionStore.rionBalance.toLocaleString()}</span>
            <button class="charge-btn" on:click={() => alert('충전 기능은 준비 중입니다. 앱에서 충전 부탁드립니다.')}>충전하기</button>
          </div>
        </div>
        <AccountBenefits />
      </div>
    {:else if activeTab === 'activity'}
      <div class="content-card"><AccountActivity /></div>
    {:else if activeTab === 'faq'}
      <div class="content-card" in:fade>
        <h2>{$t('faq')}</h2>
        <a href={publicPath($locale, 'faq')}>{$t('faq')} ↗</a>
        <div class="faq-list">
          {#each faqs as faq}
            <div class="faq-item">
              <div class="faq-q">Q. {faq.q}</div>
              <div class="faq-a">{faq.a}</div>
            </div>
          {/each}
        </div>
      </div>
    {:else if activeTab === 'contact'}
      <div class="content-card" in:fade>
        <h2>문의하기</h2>
        <p class="desc">서비스 이용 중 불편한 점이나 건의사항을 남겨주세요.</p>
        
        <div class="contact-form">
          <div class="input-group">
            <label for="contact-subject">제목</label>
            <input id="contact-subject" type="text" bind:value={contactSubject} placeholder="제목을 입력해주세요 (선택)" />
          </div>
          <div class="input-group">
            <label for="contact-message">내용</label>
            <textarea id="contact-message" bind:value={contactMessage} rows="6" placeholder="문의 내용을 자세히 적어주시면 빠른 답변이 가능합니다."></textarea>
          </div>
          <div class="input-group">
            <label for="contact-file">이미지 첨부</label>
            <div class="attach-area" id="contact-file">
              {#if contactFilePreview}
                <div class="preview">
                  <img src={contactFilePreview} alt="preview" />
                  <button class="clear" on:click={() => { contactFile = null; contactFilePreview = null; }}>×</button>
                </div>
              {:else}
                <label class="upload-btn">
                  <input type="file" accept="image/*" on:change={handleFileChange} hidden />
                  <span>+ 이미지 추가</span>
                </label>
              {/if}
            </div>
          </div>
          <button class="submit-btn" disabled={isSendingContact} on:click={submitContact}>
            {isSendingContact ? '전송 중...' : '문의 전송'}
          </button>
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .settings-language { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid #8883; }
  .settings-language h2 { margin: 0; font: inherit; font-size: 1.1rem; font-weight: 600; }
  .settings-wrapper {
    display: flex;
    max-width: 1100px;
    margin: 40px auto;
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.08);
    min-height: 700px;
    overflow: hidden;
    font-family: inherit;
    transition: all 0.3s ease;
  }

  :global(.dark) .settings-wrapper {
    background: #1e1e1e;
    box-shadow: 0 10px 40px rgba(0,0,0,0.3);
  }

  /* 사이드바 */
  .settings-sidebar {
    width: 300px;
    background: #f9f9f9;
    border-right: 1px solid #eee;
    display: flex;
    flex-direction: column;
    padding: 30px 20px;
    transition: all 0.3s ease;
  }

  :global(.dark) .settings-sidebar {
    background: #252525;
    border-right: 1px solid #333;
  }

  .sidebar-user {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 40px;
    padding: 0 10px;
  }

  .avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    overflow: hidden;
    background: #ddd;
    flex-shrink: 0;
  }

  :global(.dark) .avatar {
    background: #444;
  }

  .avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-ph {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    color: #888;
    background: #e5e5e5;
  }

  :global(.dark) .avatar-ph {
    background: #333;
    color: #888;
  }

  .user-meta .user-name {
    font-weight: 700;
    font-size: 16px;
    color: #333;
    transition: color 0.3s;
  }

  :global(.dark) .user-meta .user-name {
    color: #fff;
  }

  .user-meta .user-email {
    font-size: 12px;
    color: #888;
  }

  .sidebar-menu {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .sidebar-menu button {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 15px;
    border: none;
    background: none;
    border-radius: 10px;
    text-align: left;
    font-size: 15px;
    font-weight: 500;
    color: #666;
    cursor: pointer;
    transition: all 0.2s;
  }

  :global(.dark) .sidebar-menu button {
    color: #aaa;
  }

  .sidebar-menu button:hover {
    background: #eee;
    color: #333;
  }

  :global(.dark) .sidebar-menu button:hover {
    background: #333;
    color: #fff;
  }

  .sidebar-menu button.active {
    background: #ed6d1f;
    color: #fff;
    font-weight: 600;
  }

  .nav-divider {
    height: 1px;
    background: #eee;
    margin: 15px 10px;
  }

  :global(.dark) .nav-divider {
    background: #333;
  }

  .logout-btn {
    width: 100%;
    padding: 12px;
    background: #fff;
    border: 1px solid #ffdada;
    color: #ff5a5a;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
  }

  :global(.dark) .logout-btn {
    background: #2a1a1a;
    border: 1px solid #5a3a3a;
  }

  .logout-btn:hover {
    background: #fff5f5;
  }

  :global(.dark) .logout-btn:hover {
    background: #3a2a2a;
  }

  /* 주 콘텐츠 */
  .settings-main {
    flex: 1;
    padding: 50px;
    background: #fff;
    overflow-y: auto;
    transition: background 0.3s;
  }

  :global(.dark) .settings-main {
    background: #1e1e1e;
  }

  .content-card h2 {
    margin: 0 0 30px 0;
    font-size: 24px;
    font-weight: 800;
    color: #111;
  }

  :global(.dark) .content-card h2 {
    color: #fff;
  }

  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 40px;
  }

  .info-item {
    padding: 15px;
    background: #f8f8f8;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  :global(.dark) .info-item {
    background: #2a2a2a;
  }

  .info-item .label {
    font-size: 12px;
    color: #999;
    font-weight: 600;
  }

  .info-item .value {
    font-size: 16px;
    font-weight: 700;
    color: #333;
  }

  :global(.dark) .info-item .value {
    color: #eee;
  }

  .rion-box {
    background: #fff8f4;
    border: 1px solid #fee3d2;
    padding: 24px;
    border-radius: 16px;
  }

  :global(.dark) .rion-box {
    background: #2d1f18;
    border: 1px solid #4a3a30;
  }

  .rion-header {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 600;
    color: #ed6d1f;
    margin-bottom: 15px;
  }

  .rion-header img {
    width: 24px;
  }

  .rion-balance {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .rion-balance .num {
    font-size: 32px;
    font-weight: 900;
    color: #333;
  }

  :global(.dark) .rion-balance .num {
    color: #fff;
  }

  .charge-btn {
    background: #ed6d1f;
    color: #fff;
    border: none;
    padding: 10px 24px;
    border-radius: 25px;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.2s;
  }

  .charge-btn:hover {
    transform: scale(1.05);
  }

  /* FAQ 스타일 */
  .faq-list {
    display: flex;
    flex-direction: column;
    gap: 25px;
  }

  .faq-item {
    border-bottom: 1px solid #f0f0f0;
    padding-bottom: 20px;
  }

  :global(.dark) .faq-item {
    border-bottom: 1px solid #333;
  }

  .faq-q {
    font-size: 17px;
    font-weight: 700;
    color: #111;
    margin-bottom: 10px;
  }

  :global(.dark) .faq-q {
    color: #fff;
  }

  .faq-a {
    font-size: 15px;
    line-height: 1.6;
    color: #666;
    white-space: pre-wrap;
  }

  :global(.dark) .faq-a {
    color: #aaa;
  }

  /* 문의 스타일 */
  .desc {
    color: #888;
    margin-bottom: 30px;
  }

  .contact-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-width: 600px;
  }

  .input-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .input-group label {
    font-size: 14px;
    font-weight: 600;
    color: #555;
  }

  :global(.dark) .input-group label {
    color: #ccc;
  }

  .input-group input, .input-group textarea {
    padding: 14px;
    border: 1px solid #ddd;
    border-radius: 10px;
    font-size: 15px;
    font-family: inherit;
    transition: border-color 0.2s;
  }

  :global(.dark) .input-group input, :global(.dark) .input-group textarea {
    background: #2a2a2a;
    border: 1px solid #444;
    color: #fff;
  }

  .input-group input:focus, .input-group textarea:focus {
    border-color: #ed6d1f;
    outline: none;
  }

  .upload-btn {
    display: inline-flex;
    padding: 12px 20px;
    border: 1px dashed #bbb;
    border-radius: 10px;
    color: #777;
    cursor: pointer;
    font-size: 14px;
  }

  :global(.dark) .upload-btn {
    border: 1px dashed #555;
    color: #aaa;
  }

  .upload-btn:hover {
    background: #f9f9f9;
  }

  :global(.dark) .upload-btn:hover {
    background: #333;
  }

  .preview {
    position: relative;
    width: 100px;
    height: 100px;
  }

  .preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 10px;
  }

  .preview .clear {
    position: absolute;
    top: -8px;
    right: -8px;
    background: #333;
    color: #fff;
    border: none;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    cursor: pointer;
    font-size: 12px;
  }

  .submit-btn {
    margin-top: 10px;
    padding: 16px;
    background: #ed6d1f;
    color: #fff;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
  }

  .submit-btn:disabled {
    background: #ccc;
  }

  /* 모바일 */
  @media (max-width: 800px) {
    .settings-wrapper {
      flex-direction: column;
      margin: 0;
      border-radius: 0;
      box-shadow: none;
    }

    .settings-sidebar {
      width: 100%;
      border-right: none;
      border-bottom: 1px solid #eee;
    }

    .settings-main {
      padding: 30px 20px;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
